import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import type { Ticket, TicketInput, TicketMessage, TicketUpdate } from '../shared/types'

export const useTickets = (accountId?: string) =>
  useQuery({
    queryKey: ['tickets', accountId ?? ''],
    queryFn: () => api.get<Ticket[]>('/api/v1/tickets', { account_id: accountId }),
  })

export const useTicket = (id: string) =>
  useQuery({
    queryKey: ['tickets', id],
    queryFn: () => api.get<Ticket>(`/api/v1/tickets/${id}`),
    enabled: !!id,
  })

export function useCreateTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: TicketInput) => api.post<{ id: string }>('/api/v1/tickets', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tickets'] }),
  })
}

export function useUpdateTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: TicketUpdate & { id: string }) => api.put(`/api/v1/tickets/${id}`, body),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['tickets'] })
      void qc.invalidateQueries({ queryKey: ['tickets', v.id] })
    },
  })
}

export const useTicketMessages = (ticketId: string) =>
  useQuery({
    queryKey: ['messages', ticketId],
    queryFn: () => api.get<TicketMessage[]>('/api/v1/messages', { ticket_id: ticketId }),
    enabled: !!ticketId,
  })

export function useCreateTicketMessage(ticketId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { body: string; account_id?: string }) =>
      api.post('/api/v1/messages', { ...body, ticket_id: ticketId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['messages', ticketId] }),
  })
}
